import {
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BilledType } from '../models/billing-history.models';
import { MatterDetailsDto, MatterTaskDto } from '../models/matter.models';

/**
 * Matter shape returned by the external-API-backed search/lookup endpoints
 * (search, active, by-billing-partner, single get). Mirrors the API's
 * ExternalClientMatterDto. Distinct from the canonical MatterDto in
 * matter.models.ts, which is the base for MatterDetailsDto.
 */
export interface ExternalMatterDto {
  clientMatterNumber: string;
  matterName: string;
  clientNumber: string;
  billAttorney: string;
  supAttorney: string;
  orgAttorney: string;
  nonBillType: string;
  status: string;
  locationCode: string;
  locationDescription: string;
  practiceCode: string;
  practiceDescription: string;
  openDate: string;
  closeDate?: string;
}

export interface MatterSearchResponse {
  matters: ExternalMatterDto[];
  totalRecords: number;
  currentPage: number;
  totalPages: number;
}

export interface ClientMatterInfoDto {
  clientMatterNumber: string;
  clientNumber?: string;
  clientName?: string;
  matterName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MatterService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/matters`;

  /** Per-matter available-task feed; see getMatterTasks for why it is safe to cache. */
  private matterTaskCache = new Map<string, Observable<MatterTaskDto[]>>();

  /**
   * Search matters by various criteria
   */
  searchMatters(
    searchTerm?: string,
    contains?: string,
    clientNumber?: string,
    billingAttorneyEmployeeId?: string,
    locationCode?: string,
    pageSize?: number,
    pageNumber?: number,
  ): Observable<MatterSearchResponse> {
    let params = new HttpParams();

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (contains) {
      params = params.set('contains', contains);
    }
    if (clientNumber) {
      params = params.set('clientNumber', clientNumber);
    }
    if (billingAttorneyEmployeeId) {
      params = params.set(
        'billingAttorneyEmployeeId',
        billingAttorneyEmployeeId,
      );
    }
    if (locationCode) {
      params = params.set('locationCode', locationCode);
    }
    if (pageSize != null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (pageNumber != null) {
      params = params.set('pageNumber', pageNumber.toString());
    }

    return this.http
      .get<MatterSearchResponse>(`${this.apiUrl}/search`, { params });
  }

  /**
   * Get all matters for a billing partner by name or employee ID, resolved in a single call
   */
  getMattersByBillingPartner(
    billingPartner: string,
  ): Observable<MatterSearchResponse> {
    const params = new HttpParams().set('billingPartner', billingPartner);
    return this.http
      .get<MatterSearchResponse>(`${this.apiUrl}/by-billing-partner`, {
        params,
      });
  }

  /**
   * Get a single matter by client matter number
   */
  getMatter(clientMatterNumber: string): Observable<ExternalMatterDto> {
    return this.http
      .get<ExternalMatterDto>(`${this.apiUrl}/${clientMatterNumber}`);
  }

  /**
   * Get enriched matter details (client, billing partner, supervisor, notes)
   * by client matter number.
   */
  getMatterDetails(clientMatterNumber: string): Observable<MatterDetailsDto> {
    return this.http
      .get<MatterDetailsDto>(`${this.apiUrl}/${clientMatterNumber}/details`);
  }

  /**
   * Get the tasks available to assign on a matter, sourced from STB's dedicated matter-tasks
   * route.
   *
   * 200 with [] means there is nothing to pick, and covers both an unknown matter and a matter
   * that simply has no tasks: STB answers the same way for both, and the picker does the same
   * thing either way. 503 means the task list could not be read at all, so a retry may help; it
   * reaches callers as an ApiError with isServiceUnavailable set and the API's own wording in
   * details, so callers must handle the error path. There is deliberately no 404 on this route — it would report a transient outage
   * as a permanently missing matter.
   *
   * The matter number is percent-encoded defensively, the way
   * AssignmentService.getAssignmentsByMatter encodes it. Real numbers are
   * clientNumber-matterNumber (e.g. "10001-00001"), so this is a no-op for them and the
   * unencoded sibling reads in this file are fine as they stand — it only stops an
   * unexpected value from reshaping the path.
   */
  getMatterTasks(clientMatterNumber: string): Observable<MatterTaskDto[]> {
    // Cached per matter, the way LookupService caches its types. Every assignment row on
    // a matter-details page shares one matter, so without this, editing five rows issues
    // five identical GETs — and each one costs the API an uncached outbound call to the
    // external client-matter service just to read AvailableTasks.
    //
    // Safe to hold for the session: creating task assignments does not change which tasks
    // the MATTER offers. The picker subtracts the assignment's own tasks from this feed
    // (refreshTaskOptions), so a cached feed stays correct as rows are created.
    if (!this.matterTaskCache.has(clientMatterNumber)) {
      const request = this.http
        .get<MatterTaskDto[]>(
          `${this.apiUrl}/${encodeURIComponent(clientMatterNumber)}/tasks`,
        )
        // A failure is not cached: rxjs 7's shareReplay defaults to resetOnError, so an
        // errored source is dropped and the next subscriber re-issues the request. That
        // matters here because the external feed is the flaky dependency — a cached
        // failure would strand every later dialog on this matter in taskLoadFailed
        // (picker hidden) for the rest of the session. Pinned by the retry spec.
        .pipe(shareReplay(1));
      this.matterTaskCache.set(clientMatterNumber, request);
    }
    return this.matterTaskCache.get(clientMatterNumber)!;
  }

  /**
   * Drop the cached task feed — for one matter, or all of them. The feed is upstream data
   * this app does not own, so a cached entry can go stale if a code is withdrawn from the
   * matter; without this the only cure is a full browser reload, since reopening the
   * dialog re-reads the cache. Mirrors LookupService.clearCache.
   */
  clearMatterTaskCache(clientMatterNumber?: string): void {
    if (clientMatterNumber) {
      this.matterTaskCache.delete(clientMatterNumber);
      return;
    }
    this.matterTaskCache.clear();
  }

  /**
   * Get billing history for a matter, grouped by resource with aggregated totals.
   * @param clientMatterId The client matter ID
   * @param startDate Start date in YYYYMMDD format
   * @param endDate End date in YYYYMMDD format
   * @param billedTypes Optional billed-type filter. Omit to request all types.
   */
  getBillingHistory(
    clientMatterId: string,
    startDate: string,
    endDate: string,
    billedTypes?: BilledType,
  ): Observable<any> {
    let params = new HttpParams()
      .set('clientMatterId', clientMatterId)
      .set('startDate', startDate)
      .set('endDate', endDate);

    if (billedTypes != null) {
      params = params.set('billedTypes', billedTypes);
    }

    return this.http.get<any>(`${this.apiUrl}/billing-history`, { params });
  }

  /**
   * Get active matters by a list of client matter numbers
   */
  getActiveMatters(
    clientMatterNumbers: string[],
    pageSize?: number,
    pageNumber?: number,
  ): Observable<MatterSearchResponse> {
    let params = new HttpParams();

    if (pageSize != null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (pageNumber != null) {
      params = params.set('pageNumber', pageNumber.toString());
    }

    return this.http
      .post<MatterSearchResponse>(
        `${this.apiUrl}/active`,
        clientMatterNumbers,
        { params },
      );
  }

  /**
   * Get minimal client/matter info (client name and matter name) for a list of client matter numbers.
   * Intended for list views that display the client matter ID immediately and load names progressively.
   */
  getClientMatterInfo(
    clientMatterNumbers: string[],
  ): Observable<ClientMatterInfoDto[]> {
    return this.http
      .post<ClientMatterInfoDto[]>(
        `${this.apiUrl}/client-matter-info`,
        clientMatterNumbers,
      );
  }
}
